import type { WebViewerInstance } from '@pdftron/webviewer';
import download from 'downloadjs';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { theme, useMediaQuery } from '@frontend/legacy-components';

import {
  setMeasurementToolSnapMode,
  SnappingCheckbox,
} from 'modules/common/viewer/pdfViewer/SnappingCheckbox';
import { pdfStore } from 'modules/common/viewer/pdfViewer/WebViewerContext';
import {
  ANNOTATIONS_TOOL_NAMES,
  REDACT_SUBJECT,
  TOOLS_WITH_FILL,
} from 'modules/common/viewer/pdfViewer/data/constants';

import { SuggestionAnnotationClassType } from '../annotationCommands';
import {
  temporarilyHideSuggestionAnnotations,
  restoreSuggestionAnnotations,
} from '../annotationCommands/suggestionUtils';
import {
  applyRedactionsModal,
  rerenderPageScales,
  updateCurrentPageNumber,
} from '../data/actions';
import { getGroupedItems } from '../utils';

type UseInitializePDFViewerProps = {
  instance: WebViewerInstance | null;
  setIsLoadedByUrl: (nextIsLoaded: Record<string, boolean>) => void;
  SuggestionAnnotationClass: SuggestionAnnotationClassType | null;
  disabledElements: readonly string[];
  initialToolbarGroup?: 'View' | 'Annotate';
  canDownloadPdf: boolean;
};

export const useInitializePDFViewer = ({
  instance,
  setIsLoadedByUrl,
  SuggestionAnnotationClass,
  disabledElements,
  initialToolbarGroup,
  canDownloadPdf,
}: UseInitializePDFViewerProps) => {
  const [isNonMobile] = useMediaQuery(
    `(min-width: ${theme.breakpoints.tablet})`,
  );
  const isMobile = !isNonMobile;

  const dispatch = useDispatch();

  // handle basic initialization of UI that have mostly static (non-file specific or state) dependencies
  useEffect(() => {
    if (!instance) return;

    const allDisabledElements: string[] = [...disabledElements];
    if (!canDownloadPdf) {
      allDisabledElements.push('saveAsButton');
    }

    const { Core, UI } = instance;

    // Hide and show header items based on screen size
    if (isMobile) {
      UI.disableElements([
        'fitToWidth',
        'fitToPage',
        'marqueeZoomTool',
        'snappingCheckbox',
      ]);
    } else if (!isMobile) {
      UI.enableElements([
        'fitToWidth',
        'fitToPage',
        'marqueeZoomTool',
        'snappingCheckbox',
      ]);
    }

    UI.disableElements(allDisabledElements);

    const { annotationManager, documentViewer, Tools } = Core;

    const { Feature } = UI;

    if (initialToolbarGroup) {
      UI.setToolbarGroup(`toolbarGroup-${initialToolbarGroup}`);
    }

    Core.disableEmbeddedJavaScript();

    const fitToWidthButton = new instance.UI.Components.CustomButton({
      title: 'action.fitToWidth',
      img: 'icon-header-zoom-fit-to-width',
      dataElement: 'fitToWidth',
      onClick: () => {
        UI.setFitMode(UI.FitMode.FitWidth);
      },
    });
    const fitToPageButton = new instance.UI.Components.CustomButton({
      title: 'action.fitToPage',
      img: 'icon-header-zoom-fit-to-page',
      dataElement: 'fitToPage',
      onClick: () => {
        UI.setFitMode(UI.FitMode.FitPage);
      },
    });
    const marqueeZoomButton = new instance.UI.Components.ToolButton({
      title: 'Marquee Zoom',
      toolName: 'MarqueeZoomTool',
      dataElement: 'marqueeZoomTool',
      img: 'icon-header-zoom-marquee',
    });

    // For the below menu update, do not allow download (extraction) if user does not have file permissions to download
    const pageExtractionMenuItem = {
      type: 'customPageOperation',
      header: 'Page Manipulation',
      dataElement: 'customPageManipulation',
      operations: [
        {
          title: 'Extract',
          img: 'icon-page-manipulation-extract',
          onClick: async (selectedPageNumbers: number[]) => {
            if (selectedPageNumbers.length) {
              // Temporarily hide SuggestionAnnotations before extraction
              const { stateMap, suggestionAnnotations } =
                temporarilyHideSuggestionAnnotations(annotationManager);

              try {
                const fileData = await UI.extractPagesWithAnnotations(
                  selectedPageNumbers,
                );
                download(fileData, 'extractedDocument.pdf', 'application/pdf');
              } finally {
                // Always restore SuggestionAnnotations, even if extraction fails
                restoreSuggestionAnnotations(
                  annotationManager,
                  suggestionAnnotations,
                  stateMap,
                );
              }
            }
          },
          dataElement: 'customPageManipulationExtract',
        },
      ],
    };

    // Replace multi-select page manipulation controls menu with custom version
    // Purpose is to 'remove' unnecessary controls from the default menu
    // The custom elements are remade versions of the default menu options
    const pageMenuDivider = { type: 'divider' as const };
    const pageManipulationMenu = [
      {
        type: 'customPageOperation',
        header: 'Page Orientation',
        dataElement: 'customPageOrientation',
        operations: [
          {
            title: 'Rotate Counterclockwise',
            img: 'icon-header-page-manipulation-page-rotation-counterclockwise-line',
            onClick: (selectedPageNumbers: number[]) => {
              const doc = documentViewer.getDocument();
              selectedPageNumbers.forEach((page) => {
                doc.rotatePages([page], Core.PageRotation.E_270);
              });
            },
            dataElement: 'customPageManipulationRotateCounterclockwise',
          },
          {
            title: 'Rotate Clockwise',
            img: 'icon-header-page-manipulation-page-rotation-clockwise-line',
            onClick: (selectedPageNumbers: number[]) => {
              const doc = documentViewer.getDocument();
              selectedPageNumbers.forEach((page) => {
                doc.rotatePages([page], Core.PageRotation.E_90);
              });
            },
            dataElement: 'customPageManipulationRotateClockwise',
          },
        ],
      },
    ];

    if (canDownloadPdf) {
      pageManipulationMenu.push(pageMenuDivider as any, pageExtractionMenuItem);
    }
    UI.multiPageManipulationControls.update(pageManipulationMenu);
    UI.pageManipulationOverlay.update(pageManipulationMenu);
    // Enable selection of thumbnails (rather than just checkboxes) in multi-select mode
    // NOTE: types suggest this should be an object, but it is not based on example in docs
    //  and error message in console
    UI.ThumbnailsPanel.setThumbnailSelectionMode('thumbnail');

    // insert zoom tools in the correct place and update the header group
    const groupedLeftHeaderButtons = getGroupedItems(
      instance,
      'default-top-header',
      'groupedLeftHeaderButtons',
    );
    groupedLeftHeaderButtons?.items.splice(
      6,
      0,
      fitToWidthButton,
      fitToPageButton,
      marqueeZoomButton,
    );
    groupedLeftHeaderButtons?.setItems(groupedLeftHeaderButtons.items);

    UI.setTranslations('en', {
      'shortcut.Marquee': '(Z)',
    });

    // New hotkey for Marquee Zoom Tool ('Z')
    UI.hotkeys.on('Z', () => {
      documentViewer.setToolMode(
        documentViewer.getTool(Tools.ToolNames.MARQUEE),
      );
    });

    const disabledFeatures = [
      Feature.Download,
      Feature.NotesPanel,
      Feature.ThumbnailMerging,
      Feature.ThumbnailReordering,
    ];
    if (!canDownloadPdf) {
      disabledFeatures.push(Feature.Print);
    }
    UI.disableFeatures(disabledFeatures);

    // Add custom 'save' button for redaction
    const applyRedactionsButton = new instance.UI.Components.CustomButton({
      img: 'ic_check_black_24px',
      title: 'Apply Redactions',
      dataElement: 'applyRedactionsButton',
      onClick: () => {
        if (
          annotationManager
            .getAnnotationsList()
            .filter(({ Subject }) => Subject === REDACT_SUBJECT).length > 0
        ) {
          dispatch(applyRedactionsModal.open());
        }
      },
    });
    const redactionGroupedItems = getGroupedItems(
      instance,
      'tools-header',
      'redactionGroupedItems',
    );
    const pageRedactionIndex = redactionGroupedItems?.items.findIndex(
      (item) => item.dataElement === 'pageRedactionToggleButton',
    );
    redactionGroupedItems?.items.splice(
      pageRedactionIndex,
      0,
      applyRedactionsButton,
    );
    redactionGroupedItems?.setItems(redactionGroupedItems.items);

    // Add custom enable snapping checkbox
    const snappingCheckBoxItem = new instance.UI.Components.CustomElement({
      dataElement: 'snappingCheckbox',
      render: () => (
        <SnappingCheckbox instance={instance} pdfStore={pdfStore} />
      ),
    });
    const measurementGroupedItems = getGroupedItems(
      instance,
      'tools-header',
      'measureGroupedItems',
    );
    const countMeasurementButtonIndex =
      measurementGroupedItems?.items.findIndex(
        (item) => item.dataElement === 'countMeasurementToolButton',
      );
    measurementGroupedItems?.items.splice(
      countMeasurementButtonIndex + 1,
      0,
      snappingCheckBoxItem,
    );
    measurementGroupedItems?.setItems(measurementGroupedItems.items);

    // Add custom Rectangle annotation tool to the Annotate toolbar
    const rectangleToolButton = new instance.UI.Components.ToolButton({
      toolName: 'AnnotationCreateRectangle',
      dataElement: 'rectangleAnnotationButton',
      img: 'icon-tool-shape-rectangle',
    });
    const annotateGroupedItems = getGroupedItems(
      instance,
      'tools-header',
      'annotateGroupedItems',
    );

    if (annotateGroupedItems) {
      // Add the Rectangle tool to the beginning of the annotate toolbar
      annotateGroupedItems.items.unshift(rectangleToolButton);
      annotateGroupedItems.setItems(annotateGroupedItems.items);
    }

    const redactionToolSwitchIn = Tools.RedactionCreateTool.prototype.switchIn;
    Tools.RedactionCreateTool.prototype.switchIn = function (...args) {
      annotationManager.deleteAnnotations(
        annotationManager
          .getAnnotationsList()
          .filter(({ Subject }) => Subject !== REDACT_SUBJECT),
      );
      redactionToolSwitchIn.apply(this, args);
    };

    UI.setFitMode(UI.FitMode.FitWidth);

    const toolsWithFill = TOOLS_WITH_FILL(Tools);
    toolsWithFill.forEach((toolName) =>
      documentViewer.getTool(toolName).setStyles((currentStyle: any) => ({
        FillColor: currentStyle.StrokeColor,
        Opacity: 0.33,
      })),
    );

    const marqueeToolMouseLeftUp = Tools.MarqueeZoomTool.prototype.mouseLeftUp;

    // Set default tool return to 'TextSelect' tool
    Tools.MarqueeZoomTool.prototype.mouseLeftUp = function (...args) {
      documentViewer.setToolMode(documentViewer.getTool('TextSelect'));
      marqueeToolMouseLeftUp.apply(this, args);
    };

    documentViewer.addEventListener('toolModeUpdated', (toolModeClass) => {
      // delete redactions when switching tools
      if (ANNOTATIONS_TOOL_NAMES.includes(toolModeClass.name)) {
        annotationManager.deleteAnnotations(
          annotationManager
            .getAnnotationsList()
            .filter(({ Subject }) => Subject === REDACT_SUBJECT),
        );
      }
      // Change default mouse up behavior for Marquee Zoom Tool
      // Switches active tool back to previous after mouseUp with Marquee Zoom
      if (toolModeClass.name !== 'MarqueeZoomTool') {
        Tools.MarqueeZoomTool.prototype.mouseLeftUp = function (...args) {
          documentViewer.setToolMode(
            documentViewer.getTool(toolModeClass.name),
          );
          marqueeToolMouseLeftUp.apply(this, args);
        };
      }
    });

    // handle annotationsLoaded event
    // NOTE: this essentially represents when the viewer is ready for interaction
    //  the documentLoaded event is less reliable with registering certain things
    // NOTE: should we be using "finishedRendering"?
    //  https://docs.apryse.com/api/web/Core.DocumentViewer.html#event:finishedRendering__anchor
    documentViewer.addEventListener('annotationsLoaded', () => {
      const document = instance.Core.documentViewer.getDocument();
      // NOTE: we are manually setting the documentId in the loadDocument call
      //  Using the pdf compare tool, we do not load documents with any document
      //  ID, so if its null, we should NOT call setIsLoadedByUrl
      const url = document.getDocumentId();

      if (!url) return;

      setIsLoadedByUrl({ [url]: true });

      // set the snap to tool mode for all measurement tools
      // NOTE: we have to do this on annotationsLoaded because if we do it before
      //  this point, the measurement tools will not actually be active yet and
      //  this update of the setting will not take affect.
      setMeasurementToolSnapMode(instance, pdfStore.isSnappingEnabled);

      // rerender for an update to scales when annotations loaded
      dispatch(rerenderPageScales.request());
    });

    documentViewer.addEventListener('pageNumberUpdated', (pageNumber) => {
      dispatch(updateCurrentPageNumber({ pageNumber }));
    });
  }, [
    canDownloadPdf,
    instance,
    disabledElements,
    initialToolbarGroup,
    dispatch,
    setIsLoadedByUrl,
    isMobile,
    SuggestionAnnotationClass,
  ]);
};
